import Phaser from '../../lib/phaser.js';
import { UI_ASSET_KEYS } from '../../assets/asset-keys.js';
import { KENNEY_FUTURE_NARROW_FONT_NAME } from '../../assets/font-keys.js';
import { DIRECTION } from '../../common/direction.js';
import { DATA_MANAGER_STORE_KEYS, dataManager } from '../../utils/data-manager.js';
import { exhaustiveGuard } from '../../utils/guard.js';
import { MENU_COLOR } from './menu-config.js';

/** @type {Phaser.Types.GameObjects.Text.TextStyle} */
const MENU_TEXT_STYLE = {
  fontFamily: KENNEY_FUTURE_NARROW_FONT_NAME,
  color: '#FFFFFF',
  fontSize: '32px',
};

export class Menu {
  /** @type {Phaser.Scene} */
  #scene;
  /** @type {number} */
  #padding;
  /** @type {number} */
  #width;
  /** @type {number} */
  #height;
  /** @type {Phaser.GameObjects.Graphics} */
  #graphics;
  /** @type {Phaser.GameObjects.Container} */
  #container;
  /** @type {boolean} */
  #isVisible;
  /** @type {string[]} */
  #availableMenuOptions;
  /** @type {Phaser.GameObjects.Text[]} */
  #menuOptionsTextGameObjects;
  /** @type {number} */
  #selectedMenuOptionIndex;
  /** @type {string} */
  #selectedMenuOption;
  /** @type {Phaser.GameObjects.Image} */
  #userInputCursor;

  /**
   * @param {Phaser.Scene} scene
   * @param {string[]} menuOptions
   */
  constructor(scene, menuOptions) {
    this.#scene = scene;
    this.#padding = 4;
    this.#width = 300;
    this.#availableMenuOptions = menuOptions;
    this.#menuOptionsTextGameObjects = [];
    this.#selectedMenuOptionIndex = 0;

    // calculate height based on currently available options
    this.#height = 10 + this.#padding * 2 + this.#availableMenuOptions.length * 50;

    this.#graphics = this.#createGraphics();
    // Top-most within its scene: without an explicit depth the container drew at depth 0, so the world
    // menu rendered BEHIND world sprites/NPCs/the player. Menus (World / Party / Confirmation — the
    // battle scene uses a separate BattleMenu, unaffected) must always sit above scene content.
    this.#container = this.#scene.add.container(0, 0, [this.#graphics]).setDepth(1000);

    // update menu container with menu options
    for (let i = 0; i < this.#availableMenuOptions.length; i += 1) {
      const y = 10 + 50 * i + this.#padding;
      const textObj = this.#scene.add.text(40 + this.#padding, y, this.#availableMenuOptions[i], MENU_TEXT_STYLE);
      this.#menuOptionsTextGameObjects.push(textObj);
      this.#container.add(textObj);

      // Mouse/touch: tap a menu option to pick it. Phones have no SPACE key, so without this the
      // action menu (Select / Summary / Move / …) was a dead end on mobile — e.g. you could open it
      // to use a Potion but never confirm "Select". A tap moves the cursor to the option AND fires a
      // virtual confirm, which the owning scene's update() consumes via wasSpaceKeyPressed() exactly
      // like a keyboard OK — so one tap selects + applies end-to-end, with zero per-scene logic here.
      // Generous, thumb-sized hit area spanning the full menu width; keyboard navigation unaffected.
      textObj
        .setInteractive({
          useHandCursor: true,
          hitArea: new Phaser.Geom.Rectangle(-(40 + this.#padding) + 4, -8, this.#width - 8, 50),
          hitAreaCallback: Phaser.Geom.Rectangle.Contains,
        })
        .on(Phaser.Input.Events.POINTER_DOWN, () => this.#handleOptionPointerDown(i));
    }

    // add player input cursor
    this.#userInputCursor = this.#scene.add.image(20 + this.#padding, 28 + this.#padding, UI_ASSET_KEYS.CURSOR_WHITE);
    this.#userInputCursor.setScale(2.5);
    this.#container.add(this.#userInputCursor);

    this.hide();
  }

  /** @type {boolean} */
  get isVisible() {
    return this.#isVisible;
  }

  /** @type {string} */
  get selectedMenuOption() {
    return this.#selectedMenuOption;
  }

  /**
   * @returns {void}
   */
  show() {
    // Center the menu panel instead of pinning it to the top-right corner, where it overlapped the
    // currency/quest HUD. A centered modal-style panel reads cleanly on every viewport size.
    const view = this.#scene.cameras.main.worldView;
    const startX = Math.round(view.centerX - this.#width / 2);
    const startY = Math.round(view.centerY - this.#height / 2);

    this.#container.setPosition(startX, startY);
    this.#container.setAlpha(1);
    this.#isVisible = true;
    // Re-enable tap targets only while visible (see hide()).
    this.#setOptionsInteractive(true);
  }

  /**
   * @returns {void}
   */
  hide() {
    this.#container.setAlpha(0);
    this.#selectedMenuOptionIndex = 0;
    this.#moveMenuCursor(DIRECTION.NONE);
    this.#isVisible = false;
    // A hidden menu sits (invisibly) over the scene; its interactive option text would otherwise
    // keep stealing taps from whatever is underneath (e.g. the monster cards). Disable hit-testing
    // on the options while hidden so only a visible menu is tappable.
    this.#setOptionsInteractive(false);
  }

  /**
   * Toggle hit-testing on the option text objects (alpha alone doesn't stop Phaser hit-tests).
   * @param {boolean} enabled
   * @returns {void}
   */
  #setOptionsInteractive(enabled) {
    this.#menuOptionsTextGameObjects.forEach((textObj) => {
      if (textObj.input) {
        textObj.input.enabled = enabled;
      }
    });
  }

  /**
   * @param {import('../../common/direction.js').Direction|'OK'|'CANCEL'} input
   * @returns {void}
   */
  handlePlayerInput(input) {
    if (input === 'CANCEL') {
      this.hide();
      return;
    }

    if (input === 'OK') {
      this.#handleSelectedMenuOption();
      return;
    }

    // update selected menu option based on player input
    this.#moveMenuCursor(input);
  }

  /**
   * @returns {Phaser.GameObjects.Graphics}
   */
  #createGraphics() {
    const g = this.#scene.add.graphics();
    const menuColor = this.#getMenuColorsFromDataManager();

    g.fillStyle(menuColor.main, 1);
    g.fillRect(1, 0, this.#width - 1, this.#height - 1);
    g.lineStyle(8, menuColor.border, 1);
    g.strokeRect(0, 0, this.#width, this.#height);
    g.setAlpha(0.9);

    return g;
  }

  /**
   * @param {import('../../common/direction.js').Direction} direction
   * @returns {void}
   */
  #moveMenuCursor(direction) {
    switch (direction) {
      case DIRECTION.UP:
        this.#selectedMenuOptionIndex -= 1;
        if (this.#selectedMenuOptionIndex < 0) {
          this.#selectedMenuOptionIndex = this.#availableMenuOptions.length - 1;
        }
        break;
      case DIRECTION.DOWN:
        this.#selectedMenuOptionIndex += 1;
        if (this.#selectedMenuOptionIndex > this.#availableMenuOptions.length - 1) {
          this.#selectedMenuOptionIndex = 0;
        }
        break;
      case DIRECTION.LEFT:
      case DIRECTION.RIGHT:
        return;
      case DIRECTION.NONE:
        break;
      default:
        exhaustiveGuard(direction);
    }
    const x = 20 + this.#padding;
    const y = 28 + this.#padding + this.#selectedMenuOptionIndex * 50;

    this.#userInputCursor.setPosition(x, y);
  }

  /**
   * @returns {void}
   */
  #handleSelectedMenuOption() {
    this.#selectedMenuOption = this.#availableMenuOptions[this.#selectedMenuOptionIndex];
  }

  /**
   * Mouse/touch: a menu option was tapped. Only act while the menu is actually visible (the text
   * objects keep their interactive flag even while hidden). Move the highlight cursor to the tapped
   * option, then fire a virtual confirm so the owning scene's update() loop processes it via
   * wasSpaceKeyPressed() — i.e. exactly the same path as a keyboard OK, so no per-scene wiring is
   * needed and every consumer (party / battle / inventory) gets tap-to-confirm for free.
   * @param {number} index
   * @returns {void}
   */
  #handleOptionPointerDown(index) {
    if (!this.#isVisible) {
      return;
    }
    this.#selectedMenuOptionIndex = index;
    const x = 20 + this.#padding;
    const y = 28 + this.#padding + this.#selectedMenuOptionIndex * 50;
    this.#userInputCursor.setPosition(x, y);

    // `_controls` lives on BaseScene (protected). Fire a one-shot confirm pulse the scene's next
    // update() will consume as a SPACE press. Optional-chained so a non-BaseScene owner can't crash.
    const controls = /** @type {{ pressVirtualConfirm?: () => void }} */ (
      /** @type {any} */ (this.#scene)._controls
    );
    controls?.pressVirtualConfirm?.();
  }

  /**
   * @returns {{ main: number; border: number; }}
   */
  #getMenuColorsFromDataManager() {
    /** @type {import('../../common/options.js').MenuColorOptions} */
    const chosenMenuColor = dataManager.store.get(DATA_MANAGER_STORE_KEYS.OPTIONS_MENU_COLOR);
    if (chosenMenuColor === undefined) {
      return MENU_COLOR[1];
    }

    switch (chosenMenuColor) {
      case 0:
        return MENU_COLOR[1];
      case 1:
        return MENU_COLOR[2];
      case 2:
        return MENU_COLOR[3];
      default:
        // persisted menuColor outside 0..2 — fall back to the default color instead of crashing
        return MENU_COLOR[1];
    }
  }
}
